// Time Furniture — AI Sales CRM API client.
const BASE = process.env.REACT_APP_BACKEND_URL;
const mediaUrlCache = new Map();

export function crmToken() {
  return localStorage.getItem("tf_crm_token") || localStorage.getItem("tf_admin_token");
}

async function request(path, { method = "GET", body } = {}) {
  const res = await fetch(`${BASE}/api/crm${path}`, {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(crmToken() ? { Authorization: `Bearer ${crmToken()}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    localStorage.removeItem("tf_crm_token");
    const err = new Error("Session expired — please log in again.");
    err.status = 401;
    throw err;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = typeof data.detail === "string" ? data.detail : "Request failed";
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const crmApi = {
  staffLogin: (email, password) =>
    request(`/auth/login`, { method: "POST", body: { email, password } }),
  logout: () => request(`/auth/logout`, { method: "POST" }),
  me: () => request(`/auth/me`),
  dashboard: () => request(`/dashboard`),

  leads: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== "" && q.set(k, v));
    return request(`/leads?${q}`);
  },
  createLead: (body) => request(`/leads`, { method: "POST", body }),
  getLead: (id) => request(`/leads/${id}`),
  updateLead: (id, body) => request(`/leads/${id}`, { method: "PATCH", body }),
  deleteLead: (id) => request(`/leads/${id}`, { method: "DELETE" }),
  addRemark: (id, text) => request(`/leads/${id}/remarks`, { method: "POST", body: { text } }),
  exportCsvUrl: () => `${BASE}/api/crm/leads/export.csv`,

  conversations: (leadId) => request(`/leads/${leadId}/conversations`),
  addConversation: (leadId, body) =>
    request(`/leads/${leadId}/conversations`, { method: "POST", body }),
  recentConversations: () => request(`/conversations/recent`),

  tasks: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v != null && v !== "" && q.set(k, v));
    return request(`/tasks?${q}`);
  },
  createTask: (body) => request(`/tasks`, { method: "POST", body }),
  updateTask: (id, body) => request(`/tasks/${id}`, { method: "PATCH", body }),
  deleteTask: (id) => request(`/tasks/${id}`, { method: "DELETE" }),

  staff: () => request(`/staff`),
  createStaff: (body) => request(`/staff`, { method: "POST", body }),
  updateStaff: (id, body) => request(`/staff/${id}`, { method: "PATCH", body }),
  deleteStaff: (id) => request(`/staff/${id}`, { method: "DELETE" }),

  knowledge: () => request(`/knowledge`),
  createKnowledge: (body) => request(`/knowledge`, { method: "POST", body }),
  updateKnowledge: (id, body) => request(`/knowledge/${id}`, { method: "PATCH", body }),
  deleteKnowledge: (id) => request(`/knowledge/${id}`, { method: "DELETE" }),

  settings: () => request(`/settings`),
  saveSettings: (body) => request(`/settings`, { method: "PUT", body }),
  instagramStatus: () => request(`/integrations/instagram/status`),
  instagramSaveSecrets: (body) => request(`/integrations/instagram/secrets`, { method: "POST", body }),
  instagramConnection: () => request(`/integrations/instagram/connection`),
  instagramSync: (body) => request(`/integrations/instagram/sync`, { method: "POST", body }),
  instagramSyncStatus: () => request(`/integrations/instagram/sync-status`),
  instagramSyncSettings: (body) => request(`/integrations/instagram/sync-settings`, { method: "PUT", body }),
  instagramImportSelected: (body) => request(`/integrations/instagram/import-selected`, { method: "POST", body }),
  instagramAnalytics: () => request(`/integrations/instagram/analytics`),

  aiScore: (leadId) => request(`/leads/${leadId}/ai/score`, { method: "POST" }),
  aiSummary: (leadId) => request(`/leads/${leadId}/ai/summary`, { method: "POST" }),
  aiDraftReply: (leadId) => request(`/leads/${leadId}/ai/draft-reply`, { method: "POST" }),

  sendDigestNow: () => request(`/digest/send-now`, { method: "POST" }),
  digestPreview: () => request(`/digest/preview`),

  whatsappClicks: (days = 30) => request(`/reports/whatsapp-clicks?days=${days}`),

  sendWhatsApp: (leadId, message) =>
    request(`/whatsapp/send/${leadId}`, { method: "POST", body: { message } }),
  waInbox: () => request(`/whatsapp/inbox`),
  waMarkRead: (leadId) => request(`/whatsapp/inbox/${leadId}/read`, { method: "POST" }),
  waMonitor: () => request(`/whatsapp/monitor`),
  waErrors: () => request(`/whatsapp/errors`),
  waSendAlert: (leadId) => request(`/whatsapp/alert/${leadId}`, { method: "POST" }),
  waDiagnostics: (leadId) => request(`/whatsapp/diagnostics/${leadId}`),
  waTrace: (leadId) => request(`/whatsapp/trace/${leadId}`),
  waRetry: (leadId) => request(`/whatsapp/retry/${leadId}`, { method: "POST" }),
  // Media inbox
  fetchMediaUrl: async (mediaId) => {
    if (mediaUrlCache.has(mediaId)) return mediaUrlCache.get(mediaId);
    const res = await fetch(`${BASE}/api/crm/media/${mediaId}/file`, {
      credentials: "include",
      headers: crmToken() ? { Authorization: `Bearer ${crmToken()}` } : {},
    });
    if (!res.ok) throw new Error(`Media load failed (${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    mediaUrlCache.set(mediaId, url);
    return url;
  },
  waSendMedia: async (leadId, files, caption = "") => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("caption", caption);
    const res = await fetch(`${BASE}/api/crm/media/send/${leadId}`, {
      method: "POST",
      credentials: "include",
      headers: crmToken() ? { Authorization: `Bearer ${crmToken()}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Media send failed");
    return data;
  },
  retryTranscription: (convId) => request(`/media/transcribe-retry/${convId}`, { method: "POST" }),
  leadMedia: (leadId) => request(`/media/lead/${leadId}`),
  // Library
  kbList: (params = "") => request(`/library/kb${params}`),
  kbCreate: (body) => request(`/library/kb`, { method: "POST", body }),
  kbUpdate: (id, body) => request(`/library/kb/${id}`, { method: "PUT", body }),
  kbDelete: (id) => request(`/library/kb/${id}`, { method: "DELETE" }),
  kbTest: (question) => request(`/library/kb/test-answer`, { method: "POST", body: { question } }),
  kbAnalytics: () => request(`/library/kb/analytics`),
  kbTrends: () => request(`/library/kb/trends-digest`, { method: "POST" }),
  locationResolve: (q) => request(`/library/location-resolve?q=${encodeURIComponent(q)}`),
  mediaList: (params = "") => request(`/library/media${params}`),
  mediaCreate: (body) => request(`/library/media`, { method: "POST", body }),
  mediaUpdate: (id, body) => request(`/library/media/${id}`, { method: "PUT", body }),
  mediaDelete: (id) => request(`/library/media/${id}`, { method: "DELETE" }),
  mediaShare: (id, leadId) => request(`/library/media/${id}/share/${leadId}`, { method: "POST" }),
  libUpload: async (file, meta) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("meta", JSON.stringify(meta));
    const res = await fetch(`${BASE}/api/crm/library/media/upload`, {
      method: "POST",
      credentials: "include",
      headers: crmToken() ? { Authorization: `Bearer ${crmToken()}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
    return data;
  },
  libAutoTag: (id) => request(`/library/media/${id}/auto-tag`, { method: "POST" }),
  libSend: (leadId, itemIds, caption = "") => request(`/library/send/${leadId}`, { method: "POST", body: { item_ids: itemIds, caption } }),
  libSuggest: (leadId) => request(`/library/suggest/${leadId}`),
  libStats: () => request(`/library/stats`),
  // Sales Ops
  salesPipeline: () => request(`/sales/pipeline`),
  salesPriorities: () => request(`/sales/priorities`),
  salesAnalytics: (days = 30) => request(`/sales/analytics?days=${days}`),
  salesAppointments: () => request(`/sales/appointments`),
  salesSlots: (spId = "") => request(`/sales/appointments/slots?salesperson_id=${spId}`),
  salesCreateAppt: (body) => request(`/sales/appointments`, { method: "POST", body }),
  salesPatchAppt: (id, body) => request(`/sales/appointments/${id}`, { method: "PATCH", body }),
  salesAssign: (leadId) => request(`/sales/assign/${leadId}`, { method: "POST" }),
  salesQuoteBrief: (leadId) => request(`/sales/quote-brief/${leadId}`, { method: "POST" }),
  visualsList: (leadId) => request(`/library/visuals${leadId ? `?lead_id=${encodeURIComponent(leadId)}` : ""}`),
  insightsDashboard: () => request(`/insights/dashboard`),
  insightsConversion: () => request(`/insights/conversion`),
  insightsReviews: (leadId) => request(`/insights/reviews${leadId ? `?lead_id=${leadId}` : ""}`),
  insightsReviewNow: (leadId) => request(`/insights/reviews/run/${leadId}`, { method: "POST" }),
  insightsReplay: (leadId) => request(`/insights/replay/${leadId}`),
  insightsLearning: () => request(`/insights/learning`),
  insightsLearningSummarise: () => request(`/insights/learning/summarise`, { method: "POST" }),
  insightsBriefing: (generate) => request(`/insights/briefing${generate ? "?generate=true" : ""}`),
  insightsWeekly: (generate) => request(`/insights/weekly${generate ? "?generate=true" : ""}`),
  insightsHealth: () => request(`/insights/health`),
  sandboxChat: (body) => request(`/insights/sandbox/chat`, { method: "POST", body }),
  sandboxVisualPrompt: (body) => request(`/insights/sandbox/visual-prompt`, { method: "POST", body }),
  feedbackTags: () => request(`/insights/feedback/tags`),
  submitFeedback: (body) => request(`/insights/feedback`, { method: "POST", body }),
  feedbackStats: () => request(`/insights/feedback/stats`),
  upsellReport: (month) => request(`/insights/upsell-report${month ? `?month=${month}` : ""}`),
  aiPerformance: () => request(`/insights/ai-performance`),
  coaching: (generate) => request(`/insights/coaching${generate ? "?generate=true" : ""}`),
  visualCreate: (body) => request(`/library/visuals`, { method: "POST", body }),
  visualUpdate: (id, body) => request(`/library/visuals/${id}`, { method: "PUT", body }),
  visualSuggestPrompt: (id) => request(`/library/visuals/${id}/suggest-prompt`, { method: "POST" }),
  visualGenerate: (id, body = {}) => request(`/library/visuals/${id}/generate`, { method: "POST", body }),
  visualSend: (id, body) => request(`/library/visuals/${id}/send`, { method: "POST", body }),
  visualSave: (id) => request(`/library/visuals/${id}/save`, { method: "POST" }),
  visualReject: (id) => request(`/library/visuals/${id}/reject`, { method: "POST" }),
  visualRetry: (id) => request(`/library/visuals/${id}/retry`, { method: "POST" }),
  visualDelete: (id) => request(`/library/visuals/${id}`, { method: "DELETE" }),
  waTestMessage: (to, useTemplate = false, templateName = "hello_world") =>
    request(`/whatsapp/test`, { method: "POST", body: { to, use_template: useTemplate, template_name: templateName } }),
  waTestStatus: () => request(`/whatsapp/test-status`),
  waVerifyConnection: () => request(`/whatsapp/verify-connection`, { method: "POST" }),
  manualWaLead: (body) => request(`/whatsapp/manual-lead`, { method: "POST", body }),
  notifications: (unreadOnly = false) =>
    request(`/notifications${unreadOnly ? "?unread_only=true" : ""}`),
  markNotificationRead: (id) => request(`/notifications/${id}/read`, { method: "POST" }),
  markAllNotificationsRead: () => request(`/notifications/read-all`, { method: "POST" }),
};
